import { useEffect, useState } from "react";

/**
 * Custom hook to manage remembered user in localStorage
 *
 * @returns {Object} Remembered user data and methods
 */
export function useRememberedUser() {
  // Initialize state from localStorage
  const [rememberedUser, setRememberedUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem("rememberedUser");
      return savedUser ? JSON.parse(savedUser) : null;
    } catch (e) {
      console.error("Error parsing remembered user data", e);
      return null;
    }
  });

  // Function to save user data to localStorage
  const saveUser = (userData) => {
    if (!userData) {
      localStorage.removeItem("rememberedUser");
      setRememberedUser(null);
      return;
    }

    try {
      // Prepare minimal user data for storage
      const minimalUserData = {
        id: userData.id,
        email: userData.emailAddresses?.[0]?.emailAddress || userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        username: userData.username,
        imageUrl: userData.imageUrl,
        lastSignIn: new Date().toISOString(),
      };

      localStorage.setItem("rememberedUser", JSON.stringify(minimalUserData));
      setRememberedUser(minimalUserData);
      console.log("User data saved to localStorage for persistence");
    } catch (e) {
      console.error("Error saving user data to localStorage", e);
    }
  };

  // Function to clear remembered user
  const clearUser = () => {
    localStorage.removeItem("rememberedUser");
    setRememberedUser(null);
  };

  // Check preferences
  const hasRememberMePreference = () => {
    return localStorage.getItem("rememberMePreference") === "true";
  };

  // Debug log
  useEffect(() => {
    if (rememberedUser) {
      console.log("Using remembered user:", rememberedUser.email);
    }
  }, [rememberedUser]);

  return {
    rememberedUser,
    saveUser,
    clearUser,
    hasRememberMePreference,
  };
}

export default useRememberedUser;
